import React from "react";
import { Flex, Select, Input } from "@chakra-ui/react";

export const COUNTRY_CODES = [
  "\u{1F1F9}\u{1F1F7} +90",
  "\u{1F1E8}\u{1F1F3} +86",
  "\u{1F1FA}\u{1F1F8} +1",
  "\u{1F1EC}\u{1F1E7} +44",
  "\u{1F1E9}\u{1F1EA} +49",
  "\u{1F1EB}\u{1F1F7} +33",
  "\u{1F1EE}\u{1F1F9} +39",
  "\u{1F1EA}\u{1F1F8} +34",
  "\u{1F1F3}\u{1F1F1} +31",
  "\u{1F1E7}\u{1F1EA} +32",
  "\u{1F1E8}\u{1F1ED} +41",
  "\u{1F1E6}\u{1F1F9} +43",
  "\u{1F1F8}\u{1F1EA} +46",
  "\u{1F1F5}\u{1F1F1} +48",
  "\u{1F1F7}\u{1F1FA} +7",
  "\u{1F1FA}\u{1F1E6} +380",
  "\u{1F1E6}\u{1F1EA} +971",
  "\u{1F1F8}\u{1F1E6} +966",
  "\u{1F1F6}\u{1F1E6} +974",
  "\u{1F1F0}\u{1F1FC} +965",
  "\u{1F1EA}\u{1F1EC} +20",
  "\u{1F1EE}\u{1F1F7} +98",
  "\u{1F1EE}\u{1F1F6} +964",
  "\u{1F1E6}\u{1F1FF} +994",
  "\u{1F1EC}\u{1F1F7} +30",
  "\u{1F1E7}\u{1F1EC} +359",
  "\u{1F1F7}\u{1F1F4} +40",
  "\u{1F1EE}\u{1F1F3} +91",
  "\u{1F1EF}\u{1F1F5} +81",
  "\u{1F1F0}\u{1F1F7} +82",
  "\u{1F1F9}\u{1F1FC} +886",
  "\u{1F1ED}\u{1F1F0} +852",
  "\u{1F1F8}\u{1F1EC} +65",
  "\u{1F1F2}\u{1F1FE} +60",
  "\u{1F1EE}\u{1F1E9} +62",
  "\u{1F1F9}\u{1F1ED} +66",
  "\u{1F1FB}\u{1F1F3} +84",
  "\u{1F1E6}\u{1F1FA} +61",
  "\u{1F1E7}\u{1F1F7} +55",
  "\u{1F1F2}\u{1F1FD} +52",
  "\u{1F1FF}\u{1F1E6} +27",
  "\u{1F1E9}\u{1F1FF} +213",
  "\u{1F1F2}\u{1F1E6} +212",
];

// Expected local number digit count per country dial code
const PHONE_LENGTHS = {
  "+90": 10, "+86": 11, "+1": 10, "+44": 10, "+49": 10, "+33": 9,
  "+39": 10, "+34": 9, "+31": 9, "+32": 9, "+41": 9, "+43": 10,
  "+46": 9, "+48": 9, "+7": 10, "+380": 9, "+971": 9, "+966": 9,
  "+974": 8, "+965": 8, "+20": 10, "+98": 10, "+964": 10, "+994": 9,
  "+30": 10, "+359": 9, "+40": 9, "+91": 10, "+81": 10, "+82": 10,
  "+886": 9, "+852": 8, "+65": 8, "+60": 10, "+62": 11, "+66": 9,
  "+84": 9, "+61": 9, "+55": 11, "+52": 10, "+27": 9, "+213": 9,
  "+212": 9,
};

/**
 * Get the expected local phone number length for a country code option string.
 */
export const getPhoneLength = (countryCodeOption) => {
  const dialCode = extractDialCode(countryCodeOption);
  return PHONE_LENGTHS[dialCode] || 10;
};

/**
 * Extract the dial code (e.g. "+90") from a country code option string (e.g. "🇹🇷 +90")
 */
export const extractDialCode = (countryCodeOption) => {
  if (!countryCodeOption) return "+90";
  const match = countryCodeOption.match(/(\+\d+)/);
  return match ? match[1] : "+90";
};

/**
 * Parse a DB phone string (e.g. "+447911123456") into country code option + local number.
 * Matches longest dial code first to avoid "+1" matching "+1xxx" prefixes.
 */
export const parsePhoneToCountryAndNumber = (dbPhone) => {
  const defaultResult = { countryCode: COUNTRY_CODES[0], localNumber: "" };
  if (!dbPhone) return defaultResult;

  // Strip any non-digit/non-plus characters
  const cleaned = dbPhone.replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+")) return defaultResult;

  // Build a list of { option, dialCode } sorted by dial code length descending
  const sorted = COUNTRY_CODES.map((option) => ({
    option,
    dialCode: extractDialCode(option),
  })).sort((a, b) => b.dialCode.length - a.dialCode.length);

  for (const { option, dialCode } of sorted) {
    if (cleaned.startsWith(dialCode)) {
      return {
        countryCode: option,
        localNumber: cleaned.slice(dialCode.length),
      };
    }
  }

  return defaultResult;
};

/**
 * Format country code + local number for DB storage.
 * e.g. ("🇹🇷 +90", "5551234567") => "+905551234567"
 */
export const formatPhoneForDb = (countryCodeOption, localNumber) => {
  const dialCode = extractDialCode(countryCodeOption);
  const digits = (localNumber || "").replace(/\D/g, "");
  return digits ? dialCode + digits : "";
};

const PhoneInput = ({ id, value, onChange, countryCode, onCountryCodeChange, ...props }) => {
  const currentCode = countryCode || COUNTRY_CODES[0];
  const maxLen = getPhoneLength(currentCode);

  const handleCountryChange = (newCode) => {
    onCountryCodeChange(newCode);
    // Clear the phone number when country changes to avoid stale digits
    if (onChange) {
      onChange({ target: { value: "" } });
    }
  };

  return (
    <Flex gap={2}>
      <Select
        id={id ? id + "-cc" : undefined}
        value={currentCode}
        onChange={(e) => handleCountryChange(e.target.value)}
        width="120px"
        flexShrink={0}
        {...(props.focusBorderColor ? { focusBorderColor: props.focusBorderColor } : {})}
      >
        {COUNTRY_CODES.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </Select>
      <Input
        id={id ? id + "-number" : undefined}
        type="tel"
        value={value}
        onChange={onChange}
        maxLength={maxLen}
        placeholder={"0".repeat(maxLen)}
        {...props}
      />
    </Flex>
  );
};

export default PhoneInput;
